/**
 * DOES THIS PERSON WORK AT THIS BRANCH?
 *
 * THE PROBLEM. Two banks can have a branch with the same name. Bank of
 * Maharashtra has a Bidkin branch and Central Bank of India has a Bidkin
 * branch. Anything that matches staff to a branch by NAME alone therefore
 * puts the agent posted to one under BOTH -- which is exactly what showed
 * up as a Bank of Maharashtra agent listed under Central Bank of India
 * Bidkin.
 *
 * Hierarchy Management already answers this question correctly against a
 * branch RECORD. The screens that only know a branch NAME -- the accounts
 * scope bar, the log screens' agent picker and the commission ledger --
 * could not, so the same answer lives here, resolved from the name plus
 * the bank it was chosen under.
 *
 * The order is deliberate:
 *
 *   1. The branch's own id, when the user record carries one. Nothing can
 *      confuse that, whatever anybody renames. New and edited users store
 *      it.
 *   2. Otherwise the name AND the bank together, so two branches sharing a
 *      name stay separate.
 *
 * A user with no bank recorded still matches on the name alone. Those are
 * older records, and dropping them would empty lists that are correctly
 * staffed; they stop being ambiguous the moment the user is edited and
 * saved. This is why the fix never REMOVES somebody who belongs -- the
 * worst it can do is leave an old record in both lists, as today.
 */

export const branchKey = (v?: string | null): string => (v || '').trim().toLowerCase();

/** Every branch id this user is attached to, old field and new list alike. */
const branchIdsOf = (u: any): string[] =>
  [u?.branchId, ...((u?.branchIds as string[] | undefined) || [])].filter(
    (v): v is string => typeof v === 'string' && v.trim() !== ''
  );

/** Every branch name this user is attached to. */
const branchNamesOf = (u: any): string[] =>
  [u?.branch, ...((u?.branches as string[] | undefined) || [])].map(branchKey).filter(Boolean);

/** Every bank this user works for. */
const banksOf = (u: any): string[] =>
  [u?.bank, ...((u?.banks as string[] | undefined) || [])].map(branchKey).filter(Boolean);

/**
 * Is this user posted to the branch called `branchName` at `bankName`?
 *
 * `branchName` empty means "no branch chosen", and everybody passes.
 * `bankName` empty means the caller does not know the bank, so the check
 * falls back to the name, exactly as it behaved before.
 */
export function userInBranch(
  user: any,
  branchName: string,
  bankName: string,
  branches: any[]
): boolean {
  const wantName = branchKey(branchName);
  if (!wantName) return true;

  const wantBank = branchKey(bankName);

  // The branch records this name could mean, narrowed by bank when known.
  const candidates = (branches || []).filter((b) => {
    if (branchKey(b?.name) !== wantName) return false;
    if (!wantBank) return true;
    return branchKey(b?.bankName) === wantBank;
  });

  const theirIds = branchIdsOf(user);
  if (theirIds.length > 0 && candidates.length > 0) {
    return candidates.some((b) => theirIds.includes(b.id));
  }

  // No id on the user (or no matching branch record): fall back to the name,
  // kept honest by the bank.
  if (!branchNamesOf(user).includes(wantName)) return false;
  if (!wantBank) return true;

  const theirBanks = banksOf(user);
  if (theirBanks.length === 0) return true; // older record, nothing to disagree with
  return theirBanks.includes(wantBank);
}
